import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CheckCircle, AlertTriangle, Info, AlertOctagon, Bell } from 'lucide-react';

// Shape: { id, type, message, duration, icon, onClose }
const NotificationContext = createContext(null);

export const NotificationProvider = ({ children, max = 5 }) => {
  const [notifications, setNotifications] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => {
    setNotifications((prev) => prev.filter(n => n.id !== id));
  }, []);

  const notify = useCallback((options) => {
    const {
      type = 'info',
      message = '',
      duration = 4000,
      icon = null,
      onClose,
    } = options || {};
    if (!message) return;
    idRef.current += 1;
    const id = idRef.current;
    setNotifications((prev) => {
      const next = [...prev, { id, type, message, duration, icon, onClose }];
      // limitar cola
      if (next.length > max) next.shift();
      return next;
    });
    if (duration > 0) {
      setTimeout(() => {
        remove(id);
        onClose?.();
      }, duration + 50); // pequeño buffer
    }
    return id;
  }, [max, remove]);

  const contextValue = {
    notify,
    success: (msg, opts={}) => notify({ type: 'success', message: msg, ...opts }),
    error: (msg, opts={}) => notify({ type: 'error', message: msg, ...opts }),
    warning: (msg, opts={}) => notify({ type: 'warning', message: msg, ...opts }),
    info: (msg, opts={}) => notify({ type: 'info', message: msg, ...opts }),
    custom: (msg, opts={}) => notify({ type: 'custom', message: msg, ...opts }),
    remove,
    notifications,
  };

  const pickIcon = (n) => {
    if (n.icon) return n.icon;
    switch (n.type) {
      case 'success': return <CheckCircle className="notif-icon" />;
      case 'error': return <AlertOctagon className="notif-icon" />;
      case 'warning': return <AlertTriangle className="notif-icon" />;
      case 'info': return <Info className="notif-icon" />;
      case 'custom': return <Bell className="notif-icon" />;
      default: return <Info className="notif-icon" />;
    }
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {/* Contenedor flotante */}
      <div className="notif-wrapper" aria-live="assertive" aria-atomic="true">
        <AnimatePresence initial={false}>
          {notifications.map(n => (
            <motion.div
              key={n.id}
              layout
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 310, damping: 24 }}
              className={`notif-base notif-${n.type}`}
              role="alert"
            >
              <div className="notif-content">
                {pickIcon(n)}
                <p className="notif-text">{n.message}</p>
              </div>
              <button
                aria-label="Cerrar"
                className="notif-close"
                onClick={() => { remove(n.id); n.onClose?.(); }}
              >
                <X className="w-4 h-4" />
              </button>
              {n.duration > 0 && (
                <motion.div
                  className="notif-progress"
                  initial={{ width: '100%' }}
                  animate={{ width: 0 }}
                  transition={{ duration: n.duration/1000, ease: 'linear' }}
                />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotify = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotify debe usarse dentro de <NotificationProvider>');
  return ctx;
};

export default NotificationProvider;